import { useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import {
  clearAllCheckoutTracking,
  getCheckoutContextState,
  getCheckoutSource,
  getOrInitCheckoutSession,
  initCheckoutSession,
  trackMixpanel,
} from '@monorepo/client-common';
import {
  CartData,
  CheckoutEntryPath,
  CheckoutSource,
  EventTypes,
  FeatureFlagNames,
  isFeatureEnabled,
  isMetadataFromType,
  ProductType,
} from '@monorepo/types';
import { useApplicationConfigQuery } from '../../../../api/queries/use-application-config-query';
import { getActiveAssets } from '../../../../libs/const/cart-items';
import { calculateConvertedPrice } from '../../../../utils/price-utils';

interface UseNewOrderButtonStateProps {
  cartData?: CartData;
}

interface UseNewOrderButtonStateReturn {
  paymentsEnabled: boolean;
  handleClick: () => void;
}

/**
 * Custom hook for managing NewOrderButton state and click behavior.
 * Handles checkout session creation/resume logic and analytics tracking.
 */
export const useNewOrderButtonState = ({
  cartData,
}: UseNewOrderButtonStateProps): UseNewOrderButtonStateReturn => {
  const { data } = useApplicationConfigQuery();
  const location = useLocation();
  const cart = cartData?.cart;
  const currencies = cartData?.currencies;

  const paymentsEnabled =
    isFeatureEnabled({
      appFeatureFlags: data?.featureFlags,
      userFeatureFlags: {},
      flag: FeatureFlagNames.DisablePayments,
    }) && !data?.isBlacklisted;

  const handleClick = useCallback(() => {
    const determineCheckoutSource = (): CheckoutSource | undefined => {
      const path = location.pathname;
      if (path.startsWith('/welcome') || path === '/') {
        return CheckoutSource.Welcome;
      }
      if (path.startsWith('/overview')) {
        return CheckoutSource.MyPrograms;
      }
      return undefined;
    };

    // Compute fresh at click time
    const hasItems = cart ? getActiveAssets(cart).length > 0 : false;

    const entryPath = hasItems
      ? CheckoutEntryPath.ResumeImplicit
      : CheckoutEntryPath.NewOrder;

    // Reuse existing checkout session when cart has items (resuming checkout),
    // otherwise create a new session for fresh checkout flow.
    // Entry path reflects cart state (user's context), not session lifecycle.
    const source = determineCheckoutSource();
    let checkoutSessionId: string;

    if (hasItems) {
      checkoutSessionId = getOrInitCheckoutSession(entryPath, source);
    } else {
      clearAllCheckoutTracking();
      checkoutSessionId = initCheckoutSession(entryPath, source);
    }

    const checkoutSource = getCheckoutSource();

    // Calculate total items across all assets
    const totalCartItems = cart
      ? getActiveAssets(cart).reduce(
          (sum, asset) => sum + (cart[asset]?.items?.length ?? 0),
          0,
        )
      : 0;

    const eventProperties: Record<string, unknown> = {
      placement: 'header',
      checkout_entry_path: entryPath,
      checkout_session_id: checkoutSessionId,
      checkout_source: checkoutSource,
      checkout_context_state: getCheckoutContextState(totalCartItems),
    };

    if (hasItems && cart) {
      const activeAssets = getActiveAssets(cart);
      const firstAsset = activeAssets[0];
      if (firstAsset) {
        const cartAssetData = cart[firstAsset];
        const firstItem = cartAssetData?.items?.[0];
        const programMetadata =
          firstItem && isMetadataFromType(firstItem, ProductType.Program)
            ? firstItem.metadata
            : null;

        eventProperties.asset = firstAsset;
        eventProperties.group_name = programMetadata?.groupName || 'na';
        eventProperties.buying_power = programMetadata?.buyingPower || 0;
        eventProperties.trading_system = programMetadata?.tradingSystem || 'na';
        eventProperties.total_amount_usd = programMetadata?.priceUsd || 0;
        eventProperties.total_amount_local = calculateConvertedPrice(
          cartAssetData?.totalPrice || 0,
          cart.currency,
          currencies,
        );
        eventProperties.currency = cart.currency;
      }
    }

    trackMixpanel({
      event: EventTypes.CHECKOUT_ORDER_CLICKED,
      properties: eventProperties,
    });
  }, [cart, currencies, location.pathname]);

  return {
    paymentsEnabled,
    handleClick,
  };
};
